import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
// eslint-disable-next-line regex/invalid
import { useNotify } from "react-admin";
import { useEffect } from "react";
import { reportError } from "../backoffice.utils";
import { LocalizedError } from "../utils/LocalizedError";
import _clamp from "lodash/clamp";

export function useInitGlobalNamespace() {
  // We expose the queryClient as a global variable here, so that we don't need to pass it as a parameter
  // to helper functions in order to call queryClient.invalidateQueries() after a mutation ...
  window.queryClient = useQueryClient();
  testHelper.navigateTo = useNavigate();
  // For convenient error handling we expose the global functions handleError and runSafely ...
  // eslint-disable-next-line regex/invalid
  const notify = useNotify();

  function showErrorToast(message: string) {
    console.info(`showing error toast: ${message}`);
    // We do not auto hide error toasts, user shall be able to read the error message and copy it if needed ...
    notify(message, { type: "error", autoHideDuration: null });
  }
  window.showErrorToast = showErrorToast;

  function showInfoToast(message: string) {
    console.info(`showing info toast: ${message}`);
    // A slow reader reads about 100 words per minute (see https://de.wikipedia.org/wiki/Lesegeschwindigkeit).
    // In German the average length of a word is 6 characters (see
    // https://www.duden.de/sprachwissen/sprachratgeber/Durchschnittliche-Lange-eines-deutschen-Wortes)
    // With those assumptions we can calculate the duration the toast should be visible like this:
    const autoHideDuration = _clamp((message.length / 7) * (60000 / 100), 3000, 10000);
    notify(message, { type: "info", autoHideDuration });
  }
  window.showInfoToast = showInfoToast;

  function showSuccessToast(message: string) {
    console.info(`showing success toast: ${message}`);
    // A slow reader reads about 100 words per minute (see https://de.wikipedia.org/wiki/Lesegeschwindigkeit).
    // In German the average length of a word is 6 characters (see
    // https://www.duden.de/sprachwissen/sprachratgeber/Durchschnittliche-Lange-eines-deutschen-Wortes)
    // With those assumptions we can calculate the duration the toast should be visible like this:
    const autoHideDuration = _clamp((message.length / 7) * (60000 / 100), 3000, 10000);
    notify(message, { type: "success", autoHideDuration });
  }
  window.showSuccessToast = showSuccessToast;

  useEffect(() => {
    function handleError(displayedErrorMessage: string, error: any) {
      const localizedErrorDetails = (() => {
        if (error instanceof LocalizedError) {
          return error.message;
        }
        // Try to extract localized error details from Axios errors ...
        const { response } = error;
        if (response) {
          const { data } = response;
          if (data && typeof data.status === "number" && typeof data.detail === "string" && data.detail) {
            return data.detail;
          }
        }
      })();
      if (localizedErrorDetails) {
        // Show the given error message and the error details, *do not* report the error to Sentry ...
        if (displayedErrorMessage.endsWith(".") || displayedErrorMessage.endsWith("!")) {
          displayedErrorMessage = displayedErrorMessage.substring(0, displayedErrorMessage.length - 1);
        }
        notify(`${displayedErrorMessage}: ${localizedErrorDetails}`, { type: "error", autoHideDuration: 10000 });
      } else {
        // Report the error to Sentry and show just the given error message ...
        reportError(displayedErrorMessage, error);
        notify(displayedErrorMessage, { type: "error" });
      }
    }
    window.handleError = handleError;

    async function runSafely<T>(
      action: () => Promise<T>,
      options?: {
        errorMessage?: string;
        onError?: (error: any) => any;
        successMessage?: string;
        onSuccess?: (result: T) => any;
      },
    ) {
      let result: undefined | T;
      try {
        result = await action();
      } catch (error) {
        try {
          handleError(options?.errorMessage ?? "Fehler beim Speichern der Änderung(en).", error);
          await options?.onError?.(error);
        } catch (error) {
          try {
            reportError("Error during error handling", error);
          } catch {
            // Ignored.
          }
        }
        return;
      } finally {
        void queryClient.invalidateQueries();
      }
      const successMessage = options?.successMessage ?? "Änderung(en) erfolgreich gespeichert.";
      if (successMessage) {
        notify(successMessage, { type: "success" });
      }
      await options?.onSuccess?.(result);
      return result;
    }
    window.runSafely = runSafely;
  }, []);
}
